import {
  fetchProjectFromSDK,
  fetchPublicProjectFromSDK,
  fetchPublicProjectVisibilityFromSDK,
  unlockPublicProjectFromSDK,
  resolveEnvPublishConfig
} from './sdk.js'
import { resolveProjectParams } from './resolveProjectParams.js'

// Pull the HTTP status code out of an SDK/fetch error so callers can render
// the real error (401, 403, 500, …) instead of collapsing everything to 404.
// Checks the common shapes first (err.status, err.statusCode, err.response)
// and falls back to scanning the message for a 4xx/5xx number.
export function resolveFetchErrorStatus (err) {
  if (!err) return null
  if (typeof err.status === 'number') return err.status
  if (typeof err.statusCode === 'number') return err.statusCode
  if (typeof err.response?.status === 'number') return err.response.status
  if (typeof err.cause?.status === 'number') return err.cause.status
  const msg = (err.message || '') + ' ' + (err.cause?.message || '')
  const match = msg.match(/\b([4-5]\d{2})\b/)
  return match ? parseInt(match[1], 10) : null
}

const TRUE_VALUES = new Set(['true', '1', 'on', 'yes'])
const FALSE_VALUES = new Set(['false', '0', 'off', 'no'])
function parseFlag (v) {
  if (v === true) return true
  if (v === false) return false
  if (v == null) return null
  const lower = String(v).toLowerCase()
  if (TRUE_VALUES.has(lower)) return true
  if (FALSE_VALUES.has(lower)) return false
  return null
}

const SDK_TIMEOUT_MS = 3000
const withTimeout = (p, ms = SDK_TIMEOUT_MS) => {
  let id
  return Promise.race([
    p,
    new Promise(r => { id = setTimeout(() => r(null), ms) })
  ]).finally(() => clearTimeout(id))
}

const params = new URLSearchParams(window.location.search)
const resolved = resolveProjectParams()

// Cheap synchronous check for "user has SDK tokens in localStorage on
// THIS origin" — used to decide whether to attempt an authed fallback
// for private projects after the public route 403s. No network call.
const _hasSdkAccessToken = () => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage.getItem('symbols_access_token')
  } catch { return false }
}

export async function getProjectData ({
  owner = resolved.owner,
  key = resolved.key,
  branch = resolved.branch || 'main',
  useLocal = parseFlag(params.get('useLocal')) === true,
  useNext = parseFlag(params.get('useNext')) !== false,
  env = resolved.env
} = {}) {
  if (!owner || !key) throw new Error('Provide both owner and key')

  const projectKey = `${owner}/${key}`
  const sdkBase = { useLocal, useNext }

  // `env` maps to one of the project's configured environments
  // (listEnvironments → each with its own mode/branch/version). When no env
  // config is found, fall back to treating the env name as a branch.
  let envOpts = { branch, version: resolved.version || 'latest' }
  if (env) {
    const resolved = await withTimeout(
      resolveEnvPublishConfig(projectKey, env, sdkBase).catch(() => null)
    )
    envOpts = resolved || { branch: env, version: 'latest' }
  }

  // Preview domains serve CONSUMPTION (visibility-gated public snapshots) —
  // never the editor's authed data route. If the project isn't public the
  // loader surfaces that state rather than silently falling through to auth,
  // which would blur the orthogonality of access (editor authz) vs
  // visibility (preview serving).
  //
  // Matches production (`*.preview.symbols.app`) AND local dev
  // (`preview.localhost`, `*.preview.localhost`) so the `preview.` subdomain
  // convention stays consistent across environments.
  const host = window.location.hostname
  const isPreviewHost =
    host.endsWith('.preview.symbols.app') ||
    host === 'preview.localhost' ||
    host.endsWith('.preview.localhost')
  if (isPreviewHost) {
    // Build a fallback chain so that if the explicitly-requested env (or the
    // default "production") isn't published, we try less-formal envs in order.
    // First non-null result wins and its env key is surfaced as `_resolvedEnv`
    // so the preview chrome can show "Showing development env" banner.
    const envOrder = env
      ? [...new Set([env, 'production', 'staging', 'development'])]
      : ['production', 'staging', 'development']
    let pub = null
    let resolvedEnv = null
    let passwordRequiredEnv = null
    for (const tryEnv of envOrder) {
      try {
        pub = await fetchPublicProjectFromSDK(owner, key, { envKey: tryEnv, ...sdkBase })
        if (pub) { resolvedEnv = tryEnv; break }
      } catch (e) {
        // Password-protected short-circuits the fallback chain — the
        // project IS published in this env, the caller just lacks the
        // password. Remember the first env that demanded one so the
        // unlock POST targets the right env config.
        if (e?.code === 'password_required') {
          passwordRequiredEnv = tryEnv
          break
        }
        throw e
      }
    }
    if (pub) {
      // Surface the resolved env so PreviewNavbar's env switcher shows the
      // correct active env and can display a fallback banner when the
      // originally-requested env differed from what was actually served.
      const requestedEnv = env || 'production'
      return {
        ...pub,
        _resolvedEnv: resolvedEnv,
        _requestedEnv: requestedEnv,
        _usedFallback: resolvedEnv !== requestedEnv
      }
    }
    // Probe visibility so the error path can route to the correct UI:
    //   public            → genuine "no env published" (legacy message)
    //   private           → cross-domain login redirect
    //   password-protected → password card (or already known from 401)
    let visibility = passwordRequiredEnv ? 'password-protected' : null
    if (!visibility) {
      visibility = await fetchPublicProjectVisibilityFromSDK(owner, key, sdkBase)
    }

    // Authed-fallback path for PRIVATE projects: when the user has just
    // returned from the cross-origin handoff (or already has SDK tokens
    // on the preview origin from a prior session), try the AUTHED data
    // route. If they're a member with read access, the preview renders
    // without round-tripping through the workspace shell again.
    if (visibility === 'private' && _hasSdkAccessToken()) {
      try {
        const authed = await fetchProjectFromSDK(owner, key, { ...sdkBase, ...envOpts })
        if (authed) {
          const requestedEnv = env || 'production'
          return {
            ...authed,
            _resolvedEnv: env || envOpts.branch || 'main',
            _requestedEnv: requestedEnv,
            _usedFallback: false,
            _authed: true
          }
        }
      } catch (e) {
        // Token present but no project access → still surface the
        // private-project error so the UI prompts to switch accounts.
        // Don't break the visibility-routing branch below.
      }
    }

    const err = new Error(
      visibility === 'private'
        ? `Project "${projectKey}" is private`
        : visibility === 'password-protected'
          ? `Project "${projectKey}" is password-protected`
          : `Project "${projectKey}" has no public env (tried ${envOrder.join(', ')})`
    )
    err.code =
      visibility === 'private'
        ? 'project_private'
        : visibility === 'password-protected'
          ? 'project_password_protected'
          : 'project_not_public'
    err.visibility = visibility || 'public'
    err.triedEnvs = envOrder
    err.owner = owner
    err.key = key
    err.passwordEnv = passwordRequiredEnv || envOrder[0]
    err.signedIn = _hasSdkAccessToken()
    throw err
  }

  const data = await fetchProjectFromSDK(owner, key, { ...sdkBase, ...envOpts })
  if (data) return data
  throw new Error(`No data returned for project "${projectKey}"`)
}

// Unlock a password-protected preview. `password` is the SHA-256 hex of
// the user-typed value (matches how project-account.js stores it).
// Returns the same shape as `getProjectData` on success so the preview
// can swap state.data + re-render. Returns null on invalid password —
// caller re-prompts.
export async function unlockProjectData ({
  owner = resolved.owner,
  key = resolved.key,
  envKey,
  password,
  useLocal = parseFlag(params.get('useLocal')) === true,
  useNext = parseFlag(params.get('useNext')) !== false
} = {}) {
  if (!owner || !key) throw new Error('Provide both owner and key')
  if (!envKey) throw new Error('envKey required')
  if (!password) throw new Error('password required')
  const data = await unlockPublicProjectFromSDK(owner, key, {
    envKey, password, useLocal, useNext
  })
  if (!data) return null
  return {
    ...data,
    _resolvedEnv: envKey,
    _requestedEnv: envKey,
    _usedFallback: false,
    _unlocked: true
  }
}
